// apiService.ts - Centralized API service for Functions Management with Approvers/Reviewers
import { Function, Company, User, CreateFunctionRequest, UpdateFunctionRequest, ApiResponse } from './types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

export class FunctionsApiService {
  private getAccessToken: () => Promise<string>;

  constructor(getAccessToken: () => Promise<string>) {
    this.getAccessToken = getAccessToken;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    try {
      const token = await this.getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        return {
          success: false,
          message: errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`
        };
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API request error:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Network error'
      };
    }
  }

  // Load companies (with filtering for authorized, non-blacklisted)
  async loadCompanies(): Promise<ApiResponse<Company[]>> {
    try {
      const data = await this.makeRequest<Company[]>('/organizations');
      
      if (data.success && data.data) {
        // Filter to only authorized, non-blacklisted companies
        const authorizedCompanies = data.data.filter((company: Company) =>
          (company.authorized !== false) && (company.blacklisted !== true)
        );
        return { success: true, data: authorizedCompanies };
      } else {
        return {
          success: false,
          message: data.message || 'Failed to load companies'
        };
      }
    } catch (error) {
      return {
        success: false,
        message: 'Error loading companies'
      };
    }
  }

  // Load users by company for approver/reviewer selection
  async loadUsersByCompany(companyId: number): Promise<ApiResponse<User[]>> {
    return this.makeRequest<User[]>(`/users/company/${companyId}`);
  }

  // Load functions (now includes approvers and reviewers)
  async loadFunctions(): Promise<ApiResponse<Function[]>> {
    return this.makeRequest<Function[]>('/functions');
  }

  // Create function with approvers and reviewers
  async createFunction(functionData: CreateFunctionRequest): Promise<ApiResponse<Function>> {
    return this.makeRequest<Function>('/functioncreate', {
      method: 'POST',
      body: JSON.stringify(functionData),
    });
  }

  // Update function with approvers and reviewers
  // Note: companyId should not be included in update requests as divisions cannot change companies
  async updateFunction(functionData: UpdateFunctionRequest): Promise<ApiResponse<Function>> {
    const { functionId, ...updateData } = functionData;
    return this.makeRequest<Function>(`/functionupdate/${functionId}`, {
      method: 'PUT',
      body: JSON.stringify(updateData),
    });
  }

  // Delete function
  async deleteFunction(func: Function): Promise<ApiResponse<void>> {
    return this.makeRequest<void>(`/functiondelete/${func.functionId}`, {
      method: 'DELETE',
    });
  }
}